import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Plus, X, MessageSquare, Archive, RotateCw, Eye, BarChart3, Table2, Columns3, ChevronDown, ChevronUp, Clock, AlertTriangle, Send, Download, Upload, FileText, Trash2 } from 'lucide-react';
import RenewalAlerts from '@/components/RenewalAlerts';
import EntityPicker from '@/components/EntityPicker';

const STAGES = ['Lead', 'Qualified', 'Pitched', 'Negotiating', 'Closed Won', 'Pending Renewal', 'Closed Lost', 'Stale'];
const ACTIVE_STAGES = ['Lead', 'Qualified', 'Pitched', 'Negotiating', 'Pending Renewal'];
const STAGE_PROBS = { Lead: 10, Qualified: 25, Pitched: 50, Negotiating: 75, 'Closed Won': 100, 'Pending Renewal': 85, 'Closed Lost': 0, Stale: 0 };
const STAGE_COLORS = { Lead: '#9CA3AF', Qualified: '#3B82F6', Pitched: '#F59E0B', Negotiating: '#D97706', 'Closed Won': '#2D8B5E', 'Pending Renewal': '#BA7517', 'Closed Lost': '#D4726A', Stale: '#6B7280' };
const SOURCES = ['referral', 'inbound', 'cold_outreach', 'existing_relationship', 'event', 'other'];
const ARCHIVE_REASONS = ['Stale', 'Budget cut', 'Competitor won', 'Contact left', 'Not a fit', 'Other'];

const fmt = (v) => { if (!v) return '0'; v = Number(v); if (v >= 10000000) return `${(v/10000000).toFixed(1)}Cr`; if (v >= 100000) return `${(v/100000).toFixed(1)}L`; return v.toLocaleString('en-IN'); };

export default function PipelineModule({ divisionId, divisionName, autoOpenAddDeal = false }) {
  const [deals, setDeals] = useState([]);
  const [archivedDeals, setArchivedDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [showForm, setShowForm] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [detailDeal, setDetailDeal] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const pipeImportRef = useRef(null);
  const [updateText, setUpdateText] = useState('');
  const [archiveReason, setArchiveReason] = useState('Stale');
  const [showArchiveModal, setShowArchiveModal] = useState(null);
  const [showWinModal, setShowWinModal] = useState(null);    // deal object
  const [showLossModal, setShowLossModal] = useState(null);  // deal object
  const [showReopenModal, setShowReopenModal] = useState(null); // deal object
  const [lossReasons, setLossReasons] = useState([]);
  const [showLostView, setShowLostView] = useState(false);
  const [lostAnalytics, setLostAnalytics] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterClient, setFilterClient] = useState('all');

  const emptyForm = {
    client_company: '', client_contact_name: '', client_contact_email: '', client_contact_phone: '',
    client_location: '', industry: '', source: '', deal_name: '', deal_value: '',
    stage: 'Lead', probability: '10', expected_close: '', owner: '', notes: '',
    contract_start_date: '', contract_end_date: '',
  };
  const [form, setForm] = useState(emptyForm);

  const refresh = useCallback(async () => {
    const [dRes, sRes] = await Promise.all([
      api.get(`/pipeline?division_id=${divisionId}`),
      api.get(`/pipeline/stats?division_id=${divisionId}`),
    ]);
    setDeals(dRes.data);
    setStats(sRes.data);
    if (showArchived) {
      const aRes = await api.get(`/pipeline?division_id=${divisionId}&archived=true`);
      setArchivedDeals(aRes.data);
    }
  }, [divisionId, showArchived]);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  // Auto-open the Add Deal form when a leader clicks the "+ New Deal"
  // shortcut on the Overview tab. The shortcut sets a URL param that the
  // parent flips into this prop.
  useEffect(() => {
    if (autoOpenAddDeal) {
      setForm(emptyForm);
      setEditDeal(null);
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAddDeal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, division_id: divisionId };
    if (editDeal) {
      await api.put(`/pipeline/${editDeal}`, payload);
    } else {
      await api.post('/pipeline', payload);
    }
    setForm(emptyForm); setEditDeal(null); setShowForm(false);
    refresh();
  };

  const handleStageChange = async (dealId, newStage) => {
    await api.put(`/pipeline/${dealId}`, { stage: newStage });
    refresh();
    if (detailDeal?.id === dealId) {
      const r = await api.get(`/pipeline/${dealId}`);
      setDetailDeal(r.data);
    }
  };

  const handleAddUpdate = async (dealId) => {
    if (!updateText.trim()) return;
    await api.post(`/pipeline/${dealId}/updates`, { text: updateText });
    setUpdateText('');
    refresh();
    if (detailDeal?.id === dealId) {
      const r = await api.get(`/pipeline/${dealId}`);
      setDetailDeal(r.data);
    }
  };

  const handleArchive = async (dealId) => {
    await api.post(`/pipeline/${dealId}/archive`, { reason: archiveReason });
    setShowArchiveModal(null);
    setDetailDeal(null);
    refresh();
  };

  const handleReactivate = async (dealId) => {
    await api.post(`/pipeline/${dealId}/reactivate`);
    refresh();
  };

  const handleDelete = async (dealId, dealName) => {
    if (!window.confirm(`Delete deal "${dealName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/pipeline/${dealId}`);
      setDetailDeal(null);
      refresh();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || 'Unknown error'));
    }
  };

  const openEdit = (d) => {
    setForm({
      client_company: d.client_company || '', client_contact_name: d.client_contact_name || '',
      client_contact_email: d.client_contact_email || '', client_contact_phone: d.client_contact_phone || '',
      client_location: d.client_location || '', industry: d.industry || '', source: d.source || '',
      deal_name: d.deal_name || '', deal_value: d.deal_value || '', stage: d.stage || 'Lead',
      probability: d.probability || '10', expected_close: d.expected_close || '', owner: d.owner || '',
      notes: d.notes || '', contract_start_date: d.contract_start_date || '', contract_end_date: d.contract_end_date || '',
    });
    setEditDeal(d.id);
    setShowForm(true);
  };

  const onStageFormChange = (newStage) => {
    setForm(f => ({ ...f, stage: newStage, probability: String(STAGE_PROBS[newStage] ?? f.probability) }));
  };

  const sorted = [...deals].filter(d => {
    if (filterEntity !== 'all' && !(d.entity || '').toLowerCase().includes(filterEntity.toLowerCase())) return false;
    if (filterClient !== 'all' && d.client_company !== filterClient) return false;
    return true;
  }).sort((a, b) => {
    const av = a[sortField], bv = b[sortField];
    if (sortField === 'deal_value' || sortField === 'pipeline_value' || sortField === 'days_in_stage' || sortField === 'total_age_days') {
      return sortDir === 'asc' ? (Number(av) || 0) - (Number(bv) || 0) : (Number(bv) || 0) - (Number(av) || 0);
    }
    return sortDir === 'asc' ? String(av || '').localeCompare(String(bv || '')) : String(bv || '').localeCompare(String(av || ''));
  });

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading pipeline...</div>;

  const filteredDeals = deals.filter(d => {
    if (filterEntity !== 'all' && !(d.entity || '').toLowerCase().includes(filterEntity.toLowerCase())) return false;
    if (filterClient !== 'all' && d.client_company !== filterClient) return false;
    return true;
  });

  // Service-type groups from existing deal entities
  const entityGroups = (() => {
    const entities = Array.from(new Set(deals.map(d => (d.entity || '').trim()).filter(Boolean)));
    const groups = new Set(['all']);
    for (const e of entities) {
      const low = e.toLowerCase();
      if (low.includes('elp')) groups.add('ELP');
      if (low.includes('mailroom')) groups.add('Mailroom');
      if (low.includes('pettycash')) groups.add('Pettycash');
      if (low.includes('admin')) groups.add('Admin');
      if (low.includes('pf')) groups.add('PF Helpdesk');
    }
    return Array.from(groups);
  })();

  // Top clients by value for chip shortcut
  const topClients = Array.from(new Set(deals
    .filter(d => d.stage === 'Closed Won' || d.stage === 'Pending Renewal')
    .sort((a, b) => (b.deal_value || 0) - (a.deal_value || 0))
    .map(d => d.client_company)
    .filter(Boolean))).slice(0, 6);

  return (
    <div className="space-y-4" data-testid="pipeline-module">
      {/* Renewal alerts (only shows if there are contracts with end dates) */}
      <RenewalAlerts divisionId={divisionId} compact={true} />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {(() => {
            const activeDeals = deals.filter(d => !d.archived && !['Closed Won', 'Closed Lost'].includes(d.stage));
            const archivedCount = deals.filter(d => d.archived).length;
            const won = deals.filter(d => d.stage === 'Closed Won' && !d.archived).length;
            return (
              <>
                <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                  Pipeline ({activeDeals.length} active{archivedCount > 0 ? ` · ${archivedCount} archived` : ''}{won > 0 ? ` · ${won} won` : ''})
                </h4>
                {stats && <p className="text-xs" style={{ color: '#6B7280' }}>Weighted: {fmt(stats.weighted_pipeline)} | Active: {stats.active_deals} | Win rate: {stats.win_rate}%</p>}
              </>
            );
          })()}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* View toggle */}
          <div className="flex rounded-md border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            {[{ id: 'kanban', icon: Columns3 }, { id: 'table', icon: Table2 }, { id: 'funnel', icon: BarChart3 }].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className="px-2.5 py-1.5 text-xs flex items-center gap-1"
                style={{ backgroundColor: view === v.id ? '#1B2A4A' : '#fff', color: view === v.id ? '#fff' : '#6B7280' }}
                data-testid={`view-${v.id}`}>
                <v.icon size={12} /> {v.id.charAt(0).toUpperCase() + v.id.slice(1)}
              </button>
            ))}
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/csv-template?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pipeline-template-btn">
            <FileText size={11} /> Template
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/export-csv?division_id=${divisionId}&token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pipeline-export-btn">
            <Download size={11} /> Export
          </a>
          <button onClick={() => pipeImportRef.current?.click()} className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pipeline-import-btn">
            <Upload size={11} /> Import
          </button>
          <input ref={pipeImportRef} type="file" accept=".csv" className="hidden" onChange={async (ev) => {
            const file = ev.target.files?.[0]; if (!file) return;
            const fd = new FormData(); fd.append('file', file);
            try { const r = await api.post('/pipeline/import-csv', fd); alert(`Imported ${r.data.imported} deals`); refresh(); } catch {}
            ev.target.value = '';
          }} />
          <button onClick={() => setShowArchived(!showArchived)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50"
            style={{ borderColor: '#E5E1DC', color: showArchived ? '#D4726A' : '#6B7280' }} data-testid="toggle-archived">
            <Archive size={12} /> Archived
          </button>
          <button onClick={() => { setLostAnalytics(null); setShowLostView(true); }} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-red-50"
            style={{ borderColor: '#FEE2E2', color: '#D4726A' }} data-testid="open-lost-deals">
            📉 Lost Deals
          </button>
          <button onClick={() => { setForm(emptyForm); setEditDeal(null); setShowForm(true); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="add-deal-btn">
            <Plus size={13} /> Add Deal
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="deal-form">
          <div className="flex items-center justify-between pb-2 border-b" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{editDeal ? 'Edit Deal' : 'New Deal'}</p>
            <button type="button" onClick={() => { setShowForm(false); setEditDeal(null); }}><X size={14} style={{ color: '#6B7280' }} /></button>
          </div>
          {/* Client info */}
          <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Client Information</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Company *</label><input value={form.client_company} onChange={e => setForm(f => ({...f, client_company: e.target.value}))} required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="deal-company" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Contact Person</label><input value={form.client_contact_name} onChange={e => setForm(f => ({...f, client_contact_name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Contact Email</label><input type="email" value={form.client_contact_email} onChange={e => setForm(f => ({...f, client_contact_email: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Contact Phone</label><input value={form.client_contact_phone} onChange={e => setForm(f => ({...f, client_contact_phone: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>City</label><input value={form.client_location} onChange={e => setForm(f => ({...f, client_location: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Source</label><select value={form.source} onChange={e => setForm(f => ({...f, source: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}><option value="">Select...</option>{SOURCES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></div>
          </div>
          {/* Deal info */}
          <p className="text-[10px] font-semibold uppercase tracking-wide pt-2" style={{ color: '#9CA3AF' }}>Deal Information</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Deal Name / Description</label><input value={form.deal_name} onChange={e => setForm(f => ({...f, deal_name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="deal-name" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Deal Value (INR) *</label><input type="number" value={form.deal_value} onChange={e => setForm(f => ({...f, deal_value: e.target.value}))} required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="deal-value" /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Stage</label><select value={form.stage} onChange={e => onStageFormChange(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="deal-stage">{STAGES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Probability %</label><input type="number" min="0" max="100" value={form.probability} onChange={e => setForm(f => ({...f, probability: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Expected Close</label><input type="date" value={form.expected_close} onChange={e => setForm(f => ({...f, expected_close: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
            <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Owner</label>
              <EntityPicker
                type="people"
                returnField="title"
                value={form.owner}
                onChange={(v) => setForm(f => ({ ...f, owner: v || '' }))}
                placeholder="Search person…"
                testId="pipeline-owner-picker"
              />
            </div>
            <div className="sm:col-span-2"><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label><input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="deal-submit">{editDeal ? 'Save Changes' : 'Add Deal'}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditDeal(null); }} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* === CLIENT & SERVICE TYPE CHIPS === */}
      {(entityGroups.length > 1 || topClients.length > 1) && (
        <div className="space-y-2" data-testid="pipeline-chips">
          {entityGroups.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold mr-1" style={{ color: '#9CA3AF' }}>Service</span>
              {entityGroups.map(g => (
                <button key={g} onClick={() => setFilterEntity(g)} className={`whitespace-nowrap px-2.5 py-1 text-[11px] rounded-full transition-colors ${filterEntity === g ? 'font-semibold text-white' : 'font-medium hover:bg-gray-100'}`} style={filterEntity === g ? { backgroundColor: '#2A7B88' } : { backgroundColor: '#F3F4F6', color: '#6B7280' }} data-testid={`chip-entity-${g}`}>
                  {g === 'all' ? 'All Services' : g}
                </button>
              ))}
            </div>
          )}
          {topClients.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold mr-1" style={{ color: '#9CA3AF' }}>Top Clients</span>
              <button onClick={() => setFilterClient('all')} className={`whitespace-nowrap px-2.5 py-1 text-[11px] rounded-full transition-colors ${filterClient === 'all' ? 'font-semibold text-white' : 'font-medium hover:bg-gray-100'}`} style={filterClient === 'all' ? { backgroundColor: '#1B2A4A' } : { backgroundColor: '#F3F4F6', color: '#6B7280' }} data-testid="chip-client-all">
                All Clients
              </button>
              {topClients.map(c => (
                <button key={c} onClick={() => setFilterClient(c)} className={`whitespace-nowrap px-2.5 py-1 text-[11px] rounded-full transition-colors ${filterClient === c ? 'font-semibold text-white' : 'font-medium hover:bg-gray-100'}`} style={filterClient === c ? { backgroundColor: '#1B2A4A' } : { backgroundColor: '#F3F4F6', color: '#6B7280' }} data-testid={`chip-client-${c.replace(/[^a-z0-9]/gi, '-')}`}>
                  {c.length > 16 ? c.slice(0, 16) + '…' : c}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === KANBAN VIEW === */}
      {view === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-view">
          {ACTIVE_STAGES.map(stage => {
            const stageDeals = filteredDeals.filter(d => d.stage === stage);
            const stageValue = stageDeals.reduce((s, d) => s + (d.deal_value || 0), 0);
            return (
              <div key={stage} className="flex-shrink-0 w-72 rounded-lg border" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
                <div className="px-3 py-2.5 border-b flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                    <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{stage}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#E5E1DC', color: '#6B7280' }}>{stageDeals.length}</span>
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{fmt(stageValue)}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px] max-h-[500px] overflow-y-auto">
                  {stageDeals.map(d => (
                    <div key={d.id} className="bg-white rounded-md border p-2.5 cursor-pointer hover:shadow-sm transition-shadow"
                      style={{ borderColor: '#E5E1DC', borderLeftWidth: '3px', borderLeftColor: d.aging_alert === 'red' ? '#D4726A' : d.aging_alert === 'amber' ? '#F59E0B' : STAGE_COLORS[stage] }}
                      onClick={() => setDetailDeal(d)} data-testid={`deal-card-${d.id}`}>
                      <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{d.client_company}</p>
                      {d.division_id && d.division_id !== divisionId && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}>{d.division_id}</span>
                      )}
                      {d.deal_name && <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>{d.deal_name}</p>}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs font-semibold" style={{ color: STAGE_COLORS[stage] }}>{fmt(d.deal_value)}</span>
                        <div className="flex items-center gap-1">
                          {d.aging_alert && <AlertTriangle size={10} style={{ color: d.aging_alert === 'red' ? '#D4726A' : '#F59E0B' }} />}
                          <span className="text-[10px]" style={{ color: '#9CA3AF' }}><Clock size={9} className="inline" /> {d.days_in_stage}d</span>
                        </div>
                      </div>
                      {d.owner && <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>{d.owner}</p>}
                      {d.updates?.length > 0 && <p className="text-[10px] mt-1 truncate" style={{ color: '#6B7280' }}>{d.updates[0].text}</p>}
                    </div>
                  ))}
                  {stageDeals.length === 0 && <p className="text-[10px] text-center py-4" style={{ color: '#9CA3AF' }}>No deals</p>}
                </div>
              </div>
            );
          })}
          {/* Won/Lost columns - compact */}
          {['Closed Won', 'Closed Lost'].map(stage => {
            const stageDeals = filteredDeals.filter(d => d.stage === stage);
            return (
              <div key={stage} className="flex-shrink-0 w-56 rounded-lg border" style={{ borderColor: '#E5E1DC', backgroundColor: stage === 'Closed Won' ? '#F0FDF4' : '#FEF2F2' }}>
                <div className="px-3 py-2.5 border-b flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                    <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{stage}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#E5E1DC', color: '#6B7280' }}>{stageDeals.length}</span>
                  </div>
                </div>
                <div className="p-2 space-y-1 max-h-[400px] overflow-y-auto">
                  {stageDeals.map(d => (
                    <div key={d.id} className="rounded px-2 py-1.5 cursor-pointer hover:opacity-80"
                      style={{ backgroundColor: stage === 'Closed Won' ? '#DCFCE7' : '#FEE2E2' }}
                      onClick={() => setDetailDeal(d)}>
                      <p className="text-[10px] font-medium" style={{ color: '#1B2A4A' }}>{d.client_company}</p>
                      <span className="text-[10px]" style={{ color: STAGE_COLORS[stage] }}>{fmt(d.deal_value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* === TABLE VIEW === */}
      {view === 'table' && (
        <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="table-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                {[
                  { key: 'client_company', label: 'Client' },
                  { key: 'deal_value', label: 'Value' },
                  { key: 'stage', label: 'Stage' },
                  { key: 'probability', label: 'Prob%' },
                  { key: 'pipeline_value', label: 'Weighted' },
                  { key: 'days_in_stage', label: 'Days in Stage' },
                  { key: 'total_age_days', label: 'Age' },
                  { key: 'owner', label: 'Owner' },
                  { key: 'expected_close', label: 'Exp Close' },
                ].map(col => (
                  <th key={col.key} className="text-left px-3 py-2.5 text-xs font-medium cursor-pointer select-none hover:bg-gray-100"
                    style={{ color: '#6B7280' }} onClick={() => { setSortField(col.key); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                    {col.label} {sortField === col.key && (sortDir === 'asc' ? <ChevronUp size={10} className="inline" /> : <ChevronDown size={10} className="inline" />)}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-xs font-medium text-right" style={{ color: '#6B7280' }}>Actions</th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {sorted.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50" data-testid={`deal-row-${d.id}`}>
                    <td className="px-3 py-2"><p className="text-xs font-medium cursor-pointer hover:underline" style={{ color: '#1B2A4A' }} onClick={() => setDetailDeal(d)}>{d.client_company}</p>{d.division_id && d.division_id !== divisionId && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}>{d.division_id}</span>}{d.deal_name && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{d.deal_name}</p>}</td>
                    <td className="px-3 py-2 text-xs font-medium" style={{ color: '#1B2A4A' }}>{fmt(d.deal_value)}</td>
                    <td className="px-3 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: STAGE_COLORS[d.stage] + '20', color: STAGE_COLORS[d.stage] }}>{d.stage}</span></td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.probability}%</td>
                    <td className="px-3 py-2 text-xs font-medium" style={{ color: '#2A7B88' }}>{fmt(d.pipeline_value)}</td>
                    <td className="px-3 py-2"><span className="text-xs" style={{ color: d.aging_alert === 'red' ? '#D4726A' : d.aging_alert === 'amber' ? '#F59E0B' : '#6B7280' }}>{d.days_in_stage}d {d.aging_alert && <AlertTriangle size={10} className="inline" />}</span></td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#9CA3AF' }}>{d.total_age_days}d</td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.owner || '-'}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.expected_close || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(d)} className="text-xs px-2 py-1 rounded hover:bg-blue-50" style={{ color: '#2A7B88' }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === FUNNEL VIEW === */}
      {view === 'funnel' && (
        <FunnelView divisionId={divisionId} deals={filteredDeals} stats={stats} />
      )}

      {/* === ARCHIVED DEALS === */}
      {showArchived && archivedDeals.length > 0 && (
        <div className="bg-gray-50 rounded-lg border p-4 space-y-2" style={{ borderColor: '#E5E1DC' }} data-testid="archived-section">
          <p className="text-xs font-semibold" style={{ color: '#6B7280' }}>Archived Deals ({archivedDeals.length})</p>
          {archivedDeals.map(d => (
            <div key={d.id} className="flex items-center justify-between bg-white rounded px-3 py-2 border" style={{ borderColor: '#E5E1DC' }}>
              <div>
                <p className="text-xs font-medium" style={{ color: '#6B7280' }}>{d.client_company}</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{fmt(d.deal_value)} — {d.archive_reason || 'Archived'}</p>
              </div>
              <button onClick={() => handleReactivate(d.id)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded hover:bg-blue-50" style={{ color: '#2A7B88' }} data-testid={`reactivate-${d.id}`}>
                <RotateCw size={10} /> Reactivate
              </button>
            </div>
          ))}
        </div>
      )}

      {/* === DEAL DETAIL PANEL === */}
      {detailDeal && (
        <DealDetailPanel deal={detailDeal} onClose={() => setDetailDeal(null)}
          onStageChange={handleStageChange} onEdit={openEdit}
          onAddUpdate={handleAddUpdate} updateText={updateText} setUpdateText={setUpdateText}
          onArchive={(id) => setShowArchiveModal(id)} onDelete={handleDelete}
          onWin={(d) => setShowWinModal(d)}
          onLose={(d) => setShowLossModal(d)}
          onReopen={(d) => setShowReopenModal(d)} />
      )}

      {/* Win / Loss / Reopen modals */}
      {showWinModal && (
        <WinDealModal deal={showWinModal} onClose={() => setShowWinModal(null)}
          onSuccess={async (msg) => {
            setShowWinModal(null);
            setDetailDeal(null);
            await refresh();
            // eslint-disable-next-line no-alert
            window.alert(msg);
          }} />
      )}
      {showLossModal && (
        <LossDealModal deal={showLossModal} onClose={() => setShowLossModal(null)}
          reasons={lossReasons} setReasons={setLossReasons}
          onSuccess={async () => {
            setShowLossModal(null);
            setDetailDeal(null);
            await refresh();
          }} />
      )}
      {showReopenModal && (
        <ReopenDealModal deal={showReopenModal} onClose={() => setShowReopenModal(null)}
          onSuccess={async () => {
            setShowReopenModal(null);
            setDetailDeal(null);
            await refresh();
          }} />
      )}
      {showLostView && (
        <LostDealsView divisionId={divisionId} onClose={() => setShowLostView(false)}
          analytics={lostAnalytics} setAnalytics={setLostAnalytics}
          onReopen={(d) => { setShowLostView(false); setShowReopenModal(d); }} />
      )}

      {/* Archive modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowArchiveModal(null)}>
          <div className="bg-white rounded-lg p-5 w-80 space-y-3" onClick={e => e.stopPropagation()} data-testid="archive-modal">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Archive Deal</p>
            <div><label className="text-xs" style={{ color: '#6B7280' }}>Reason</label><select value={archiveReason} onChange={e => setArchiveReason(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>{ARCHIVE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
            <div className="flex gap-2">
              <button onClick={() => handleArchive(showArchiveModal)} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#D4726A' }}>Archive</button>
              <button onClick={() => setShowArchiveModal(null)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function DealDetailPanel({ deal, onClose, onStageChange, onEdit, onAddUpdate, updateText, setUpdateText, onArchive, onDelete, onWin, onLose, onReopen }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="deal-detail-panel">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{deal.client_company}</p>
            <div className="flex items-center gap-2">
              {deal.deal_name && <p className="text-xs" style={{ color: '#6B7280' }}>{deal.deal_name}</p>}
              {deal.division_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}>{deal.division_id}</span>}
            </div>
          </div>
          <button onClick={onClose}><X size={18} style={{ color: '#6B7280' }} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Stage + Value */}
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold" style={{ color: STAGE_COLORS[deal.stage] }}>{fmt(deal.deal_value)}</span>
            <select value={deal.stage} onChange={e => onStageChange(deal.id, e.target.value)}
              className="px-3 py-1.5 rounded-md border text-xs font-medium" style={{ borderColor: STAGE_COLORS[deal.stage], color: STAGE_COLORS[deal.stage] }} data-testid="detail-stage-select">
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-md p-2 text-center" style={{ backgroundColor: '#F7F5F2' }}><p className="text-[10px]" style={{ color: '#6B7280' }}>Probability</p><p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{deal.probability}%</p></div>
            <div className="rounded-md p-2 text-center" style={{ backgroundColor: '#F7F5F2' }}><p className="text-[10px]" style={{ color: '#6B7280' }}>Pipeline Value</p><p className="text-sm font-bold" style={{ color: '#2A7B88' }}>{fmt(deal.pipeline_value)}</p></div>
            <div className="rounded-md p-2 text-center" style={{ backgroundColor: deal.aging_alert === 'red' ? '#FEF2F2' : deal.aging_alert === 'amber' ? '#FFFBEB' : '#F7F5F2' }}>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Days in Stage</p>
              <p className="text-sm font-bold" style={{ color: deal.aging_alert === 'red' ? '#D4726A' : deal.aging_alert === 'amber' ? '#F59E0B' : '#1B2A4A' }}>{deal.days_in_stage}</p>
            </div>
          </div>

          {/* Client info */}
          {(deal.client_contact_name || deal.client_contact_email || deal.client_location) && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Client</p>
              {deal.client_contact_name && <p className="text-xs" style={{ color: '#1B2A4A' }}>{deal.client_contact_name}</p>}
              {deal.client_contact_email && <p className="text-xs" style={{ color: '#6B7280' }}>{deal.client_contact_email}</p>}
              {deal.client_contact_phone && <p className="text-xs" style={{ color: '#6B7280' }}>{deal.client_contact_phone}</p>}
              {deal.client_location && <p className="text-xs" style={{ color: '#6B7280' }}>{deal.client_location}</p>}
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span style={{ color: '#9CA3AF' }}>Owner:</span> <span style={{ color: '#1B2A4A' }}>{deal.owner || '-'}</span></div>
            <div><span style={{ color: '#9CA3AF' }}>Source:</span> <span style={{ color: '#1B2A4A' }}>{deal.source?.replace(/_/g, ' ') || '-'}</span></div>
            <div><span style={{ color: '#9CA3AF' }}>Expected Close:</span> <span style={{ color: '#1B2A4A' }}>{deal.expected_close || '-'}</span></div>
            <div><span style={{ color: '#9CA3AF' }}>Total Age:</span> <span style={{ color: '#1B2A4A' }}>{deal.total_age_days} days</span></div>
          </div>
          {deal.notes && <div className="text-xs p-2 rounded" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{deal.notes}</div>}

          {/* Update log — the deal's heartbeat */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Updates</p>
            <div className="flex gap-2">
              <input value={updateText} onChange={e => setUpdateText(e.target.value)} placeholder="Add an update..."
                className="flex-1 px-3 py-2 rounded-md border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="deal-update-input"
                onKeyDown={e => { if (e.key === 'Enter' && updateText.trim()) { onAddUpdate(deal.id); } }} />
              <button onClick={() => onAddUpdate(deal.id)} className="px-3 py-2 rounded-md text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="deal-update-submit"><Send size={12} /></button>
            </div>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {(deal.updates || []).map((u, i) => (
                <div key={i} className="text-xs px-2.5 py-1.5 rounded" style={{ backgroundColor: '#F7F5F2' }}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium" style={{ color: '#1B2A4A' }}>{u.by}</span>
                    <span style={{ color: '#9CA3AF' }}>{new Date(u.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                  </div>
                  <p style={{ color: '#374151' }}>{u.text}</p>
                </div>
              ))}
              {(!deal.updates || deal.updates.length === 0) && <p className="text-xs py-2 text-center" style={{ color: '#9CA3AF' }}>No updates yet. Add the first one.</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t flex-wrap" style={{ borderColor: '#E5E1DC' }}>
            {!deal.archived && deal.stage !== 'Closed Won' && deal.stage !== 'Closed Lost' && (
              <>
                <button
                  onClick={() => onWin(deal)}
                  className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: '#2D8B5E' }}
                  data-testid="mark-won-btn"
                >🏆 Mark as Won</button>
                <button
                  onClick={() => onLose(deal)}
                  className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold border hover:bg-red-50"
                  style={{ borderColor: '#FEE2E2', color: '#D4726A' }}
                  data-testid="mark-lost-btn"
                >❌ Mark as Lost</button>
              </>
            )}
            {(deal.stage === 'Closed Lost' || deal.stage === 'Stale' || deal.archived) && (
              <button
                onClick={() => onReopen(deal)}
                className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold border hover:bg-blue-50"
                style={{ borderColor: '#DBEAFE', color: '#3B82F6' }}
                data-testid="reopen-deal-btn"
              ><RotateCw size={11} /> Reopen Deal</button>
            )}
            <button onClick={() => onEdit(deal)} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>Edit All Fields</button>
            {!deal.archived && deal.stage !== 'Closed Won' && (
              <button onClick={() => onArchive(deal.id)} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs border hover:bg-red-50" style={{ borderColor: '#E5E1DC', color: '#D4726A' }} data-testid="archive-deal-btn"><Archive size={11} /> Archive</button>
            )}
            {onDelete && (
              <button onClick={() => onDelete(deal.id, deal.deal_name || deal.client_company)} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs border hover:bg-red-50 ml-auto" style={{ borderColor: '#FEE2E2', color: '#D4726A' }} data-testid="delete-deal-btn"><Trash2 size={11} /> Delete</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function FunnelView({ divisionId, deals, stats }) {
  const funnelStages = ['Lead', 'Qualified', 'Pitched', 'Negotiating', 'Closed Won'];
  const maxCount = Math.max(...funnelStages.map(s => deals.filter(d => d.stage === s).length), 1);

  return (
    <div className="bg-white rounded-lg border p-5 space-y-4" style={{ borderColor: '#E5E1DC' }} data-testid="funnel-view">
      <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Pipeline Funnel</p>
      <div className="space-y-2">
        {funnelStages.map((stage, i) => {
          const stageDeals = deals.filter(d => d.stage === stage);
          const count = stageDeals.length;
          const value = stageDeals.reduce((s, d) => s + (d.deal_value || 0), 0);
          const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
          const prevStageDeals = i > 0 ? deals.filter(d => d.stage === funnelStages[i - 1]).length : null;
          const conversion = prevStageDeals > 0 ? Math.round(count / prevStageDeals * 100) : null;

          return (
            <div key={stage} className="flex items-center gap-3">
              <div className="w-24 text-right">
                <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{stage}</p>
                {conversion !== null && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{conversion}% conv</p>}
              </div>
              <div className="flex-1">
                <div className="h-8 rounded-md flex items-center relative" style={{ width: `${Math.max(pct, 8)}%`, backgroundColor: STAGE_COLORS[stage] + '30', transition: 'width 0.3s' }}>
                  <div className="h-full rounded-md" style={{ width: `${Math.max(pct, 8)}%`, backgroundColor: STAGE_COLORS[stage] + '60' }} />
                  <span className="absolute left-2 text-[10px] font-semibold" style={{ color: STAGE_COLORS[stage] }}>{count} deals</span>
                </div>
              </div>
              <div className="w-20 text-right">
                <span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{fmt(value)}</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Summary stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
          <div className="text-center"><p className="text-[10px]" style={{ color: '#6B7280' }}>Active Pipeline</p><p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fmt(stats.total_pipeline_value)}</p></div>
          <div className="text-center"><p className="text-[10px]" style={{ color: '#6B7280' }}>Weighted</p><p className="text-sm font-bold" style={{ color: '#2A7B88' }}>{fmt(stats.weighted_pipeline)}</p></div>
          <div className="text-center"><p className="text-[10px]" style={{ color: '#6B7280' }}>Win Rate</p><p className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{stats.win_rate}%</p></div>
          <div className="text-center"><p className="text-[10px]" style={{ color: '#6B7280' }}>Won This Month</p><p className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{stats.won_this_month?.count || 0}</p></div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// WIN / LOSS / REOPEN MODALS — Atomic deal lifecycle (2026-04-25)
// Shared across all 4 contract divisions (CC-Clubs, CC-Ops, CS-B2B, LC).
// ============================================================

function WinDealModal({ deal, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [annualValue, setAnnualValue] = useState(deal.deal_value || '');
  const [startDate, setStartDate] = useState(deal.contract_start_date || today);
  const [numRows, setNumRows] = useState(12);
  const [frequency, setFrequency] = useState('monthly');
  const [firstPayment, setFirstPayment] = useState(deal.contract_start_date || today);
  const [clientName, setClientName] = useState(deal.client_company || '');
  const [submitting, setSubmitting] = useState(false);

  const av = Number(annualValue) || 0;
  const perRow = numRows > 0 ? Math.round(av / numRows) : 0;

  const submit = async () => {
    if (!clientName.trim()) { window.alert('Client name required'); return; }
    if (av <= 0) { window.alert('Annual value must be > 0'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/pipeline/${deal.id}/mark-won`, {
        client_name: clientName.trim(),
        annual_value: av,
        start_date: startDate,
        first_payment_date: firstPayment,
        num_rows: Number(numRows) || 12,
        frequency,
      });
      onSuccess(res.data.message + (res.data.first_payment_due ? ` First payment ₹${fmt(res.data.first_payment_amount)} due ${res.data.first_payment_due}.` : ''));
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-lg space-y-4" onClick={e => e.stopPropagation()} data-testid="win-deal-modal">
        <div>
          <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>🏆 Mark Deal as Won</h3>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Deal: <span className="font-medium">{deal.deal_name || deal.client_company}</span> · {fmt(deal.deal_value)} · {deal.owner}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-md p-3 text-xs" style={{ color: '#166534' }}>
          ✅ This will create a contract, payment schedule, and (if missing) a client registry entry — all atomically.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Client name</label>
            <input value={clientName} onChange={e => setClientName(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="win-client-name" />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Annual value (₹)</label>
            <input type="number" value={annualValue} onChange={e => setAnnualValue(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="win-annual-value" />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Start date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>First payment due</label>
            <input type="date" value={firstPayment} onChange={e => setFirstPayment(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Number of rows</label>
            <input type="number" value={numRows} onChange={e => setNumRows(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-3 text-xs" style={{ color: '#374151' }}>
          Each row will be <span className="font-semibold">₹{fmt(perRow)}</span> ({fmt(av)} ÷ {numRows} rows · {frequency})
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#2D8B5E' }} data-testid="confirm-win-btn">
            {submitting ? 'Closing…' : '✅ Win the Deal & Create Contract'}
          </button>
        </div>
      </div>
    </div>
  );
}


function LossDealModal({ deal, onClose, onSuccess, reasons, setReasons }) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [lostTo, setLostTo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (reasons.length === 0) {
      api.get('/pipeline/lost-deals/reasons').then(r => setReasons(r.data.reasons));
    }
  }, [reasons.length, setReasons]);

  const submit = async () => {
    if (!reason) { window.alert('Please select a loss reason'); return; }
    setSubmitting(true);
    try {
      await api.post(`/pipeline/${deal.id}/mark-lost`, {
        lost_reason: reason,
        lost_reason_detail: detail.trim(),
        lost_to: lostTo.trim(),
      });
      onSuccess();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="loss-deal-modal">
        <div>
          <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>❌ Mark Deal as Lost</h3>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Deal: <span className="font-medium">{deal.deal_name || deal.client_company}</span> · {fmt(deal.deal_value)} · {deal.owner}</p>
        </div>
        <div>
          <label className="text-[11px] block mb-2 font-medium" style={{ color: '#1B2A4A' }}>Why did we lose this deal? *</label>
          <div className="space-y-1.5">
            {reasons.map(r => (
              <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                <input type="radio" name="lost-reason" value={r.id} checked={reason === r.id} onChange={() => setReason(r.id)} />
                <span className="text-sm" style={{ color: '#374151' }}>{r.label}</span>
              </label>
            ))}
          </div>
        </div>
        {reason === 'competitor_won' && (
          <div>
            <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Lost to which competitor?</label>
            <input value={lostTo} onChange={e => setLostTo(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="lost-to-input" />
          </div>
        )}
        <div>
          <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Notes (optional)</label>
          <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          <button onClick={submit} disabled={submitting || !reason} className="px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#D4726A' }} data-testid="confirm-loss-btn">
            {submitting ? 'Saving…' : 'Mark as Lost'}
          </button>
        </div>
      </div>
    </div>
  );
}


function ReopenDealModal({ deal, onClose, onSuccess }) {
  const [newStage, setNewStage] = useState('Qualified');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/pipeline/${deal.id}/reopen`, {
        new_stage: newStage,
        reopen_reason: reason.trim(),
      });
      onSuccess();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="reopen-deal-modal">
        <div>
          <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>🔄 Reopen Deal</h3>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>{deal.deal_name || deal.client_company} · was {deal.stage}</p>
        </div>
        <div>
          <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Reopen at stage</label>
          <select value={newStage} onChange={e => setNewStage(e.target.value)} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
            {ACTIVE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>Why are we reopening?</label>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Prospect re-engaged · Budget unblocked · etc." className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#3B82F6' }} data-testid="confirm-reopen-btn">
            {submitting ? 'Reopening…' : 'Reopen Deal'}
          </button>
        </div>
      </div>
    </div>
  );
}


function LostDealsView({ divisionId, onClose, analytics, setAnalytics, onReopen }) {
  useEffect(() => {
    if (!analytics) {
      api.get(`/pipeline/lost-deals/analytics?division_id=${divisionId}`).then(r => setAnalytics(r.data));
    }
  }, [analytics, divisionId, setAnalytics]);

  const reasonLabel = {
    price_too_high: 'Price too high',
    competitor_won: 'Competitor won',
    budget_cut: 'Budget cut',
    timing_wrong: 'Timing wrong',
    champion_left: 'Champion left',
    no_decision: 'No decision',
    built_in_house: 'Built in-house',
    other: 'Other',
    unspecified: 'Unspecified',
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="lost-deals-view">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>📉 Lost Deals — Insights</h3>
            <p className="text-xs" style={{ color: '#6B7280' }}>Why we&apos;re losing. So we can fix it.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {!analytics && <p className="p-5 text-sm" style={{ color: '#9CA3AF' }}>Loading…</p>}
        {analytics && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
                <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Lost Deals</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#D4726A' }}>{analytics.total_lost}</p>
              </div>
              <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
                <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Lost Value</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#D4726A' }}>{fmt(analytics.total_value)}</p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Loss reasons</p>
              <div className="space-y-1.5">
                {analytics.reasons.map(r => (
                  <div key={r.reason} className="flex items-center gap-3">
                    <div className="w-32 text-xs" style={{ color: '#374151' }}>{reasonLabel[r.reason] || r.reason}</div>
                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full" style={{ width: `${r.pct}%`, backgroundColor: '#D4726A' }} />
                    </div>
                    <div className="w-20 text-right text-xs" style={{ color: '#6B7280' }}>{r.count} · {r.pct}%</div>
                    <div className="w-20 text-right text-xs" style={{ color: '#6B7280' }}>{fmt(r.value)}</div>
                  </div>
                ))}
                {analytics.reasons.length === 0 && <p className="text-xs" style={{ color: '#9CA3AF' }}>No lost deals yet — keep it that way.</p>}
              </div>
            </div>

            {analytics.competitors.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Lost to competitors</p>
                <div className="flex flex-wrap gap-2">
                  {analytics.competitors.map(c => (
                    <span key={c.name} className="px-2 py-1 rounded-md text-xs" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>{c.name} ({c.count})</span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>All lost deals</p>
              <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: '#F9F7F4' }}>
                    <tr>
                      <th className="text-left px-3 py-2 text-xs" style={{ color: '#6B7280' }}>Deal</th>
                      <th className="text-left px-3 py-2 text-xs" style={{ color: '#6B7280' }}>Owner</th>
                      <th className="text-left px-3 py-2 text-xs" style={{ color: '#6B7280' }}>Reason</th>
                      <th className="text-right px-3 py-2 text-xs" style={{ color: '#6B7280' }}>Value</th>
                      <th className="text-right px-3 py-2 text-xs" style={{ color: '#6B7280' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.deals.map((d, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                        <td className="px-3 py-2 text-sm" style={{ color: '#1B2A4A' }}>{d.deal_name || d.client_company}</td>
                        <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.owner || '—'}</td>
                        <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{reasonLabel[d.lost_reason] || '—'}</td>
                        <td className="px-3 py-2 text-right text-sm" style={{ color: '#1B2A4A' }}>{fmt(d.deal_value)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => onReopen({ ...d, id: d.id || d._id })} className="text-xs px-2 py-1 rounded border hover:bg-blue-50" style={{ borderColor: '#DBEAFE', color: '#3B82F6' }}>Reopen</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
